// ============================================================
// usePlateClock - the plate's one animation loop (plan body-map S1; ADR
// 2026-10-09: one clock, no SMIL). The plate draws every animated attribute
// at its STILL value and marks the element with `data-track` (its tracks as
// JSON, engine/plate/tracks). While something on the plate moves and the
// screen plays motion, this samples each track once a frame and sets the
// attribute; nothing renders through React per frame. With motion off it
// never starts, so the still values are what the viewer sees - and when it
// stops (motion switched off mid-play, or nothing left to move) every track
// is put back to its still value, and the plate's own `onStop` resets what a
// track cannot say.
//
// WHAT IT COSTS (the perf overhaul, Stage 1). Each frame's write makes the
// browser lay out and repaint the whole figure, filters and all: one swaying
// plate held a laptop's main thread at 75%. So a plate nobody can see draws
// nothing (an IntersectionObserver), and when the only thing moving is the
// slow idle sway (`idleOnly`) it draws twenty frames a second, not sixty -
// a hit, a look or a condition still gets every frame.
// ============================================================

import { useEffect, useEffectEvent, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { attrNumber, sampleTrack, stillOf, type Track } from '../../engine/plate/tracks';
import useDisplayClock from '../../hooks/useDisplayClock';
import useHoldsStill from '../../hooks/useHoldsStill';

/** The attribute that carries an element's tracks. */
const TRACK_ATTR = 'data-track';

/** What a tracked element is to the clock: its attributes. */
interface Tracked {
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
}

/** One tracked element, found once per render: its tracks, and the last value written for each. */
interface Entry {
  el: Tracked;
  tracks: Track[];
  written: string[];
}

/** Every tracked element under `root`, its tracks parsed. Done after each render - never per frame. */
function collectTracks(root: SVGSVGElement): Entry[] {
  return [...root.querySelectorAll(`[${TRACK_ATTR}]`)].map((el) => ({
    el,
    tracks: JSON.parse(el.getAttribute(TRACK_ATTR) ?? '[]') as Track[],
    written: [],
  }));
}

/**
 * Write each track's value (a sample, or the still value with no time) - only where it changed: the discrete tracks
 * hold for a fifth of a second, and a filter re-runs over the whole figure on every write.
 */
function writeTracks(entries: Entry[], seconds?: number): void {
  for (const { el, tracks, written } of entries) {
    tracks.forEach((t, k) => {
      const value = attrNumber(seconds === undefined ? stillOf(t) : sampleTrack(t, seconds));
      if (written[k] !== value) el.setAttribute(t.attr, (written[k] = value));
    });
  }
}

/** Set every tracked attribute under `root` to its value `seconds` in. */
export const applyTracks = (root: SVGSVGElement, seconds: number) => writeTracks(collectTracks(root), seconds);

/** Put every tracked attribute under `root` back to its still value. */
export const stillTracks = (root: SVGSVGElement) => writeTracks(collectTracks(root));

/** A `data-track` attribute's value for these tracks. */
const trackAttr = (tracks: Track[]) => JSON.stringify(tracks);

/** An element's props for its tracks: each attribute at its still value, and the tracks for the clock. */
export function tracked(tracks: Track[]): Record<string, string | number> {
  return { ...Object.fromEntries(tracks.map((t) => [t.attr, stillOf(t)])), 'data-track': trackAttr(tracks) };
}

interface ClockHooks {
  /** Each frame, before the tracks: what a track cannot say (the goo's drops, the idle's transform). Seconds. */
  onFrame?: (seconds: number, root: SVGSVGElement) => void;
  /** When the clock stops after running: put what `onFrame` moved back to its still form. */
  onStop?: (root: SVGSVGElement) => void;
}

/**
 * How often the idle sway alone is drawn: twenty frames a second, smooth for a motion this slow. Less a frame's
 * jitter: three 60 Hz frames come to 49.99 ms, and a strict 50 drew every fourth (15 a second).
 */
const IDLE_FRAME_MS = 50 - 2;

/**
 * Whether the plate is on screen: an IntersectionObserver on its svg, watched again whenever the svg is a new one
 * (the figure's art arrives after the first render). Without the API (jsdom), always.
 */
function useOnScreen(root: RefObject<SVGSVGElement | null>): boolean {
  const [seen, setSeen] = useState(true);
  const watched = useRef<{ el: SVGSVGElement; io: { disconnect(): void } } | null>(null);
  useEffect(() => {
    const el = root.current;
    if (!el || watched.current?.el === el || typeof window.IntersectionObserver === 'undefined') return;
    watched.current?.io.disconnect();
    const io = new window.IntersectionObserver((entries) => setSeen(entries.some((e) => e.isIntersecting)));
    io.observe(el);
    watched.current = { el, io };
  });
  // Unmounted: disconnect AND forget it, so a mount after (StrictMode's mount, unmount, mount) observes afresh rather
  // than keeping a dead observer (the review). Keyed on state instead, the observer cost a swaying plate's page 40%
  // more main thread (S9 75% busy against 33%, measured; cause not found).
  useEffect(
    () => () => {
      watched.current?.io.disconnect();
      watched.current = null;
    },
    [],
  );
  return seen;
}

/** Calls `stopped` with the plate's svg when the clock stops after having run - never for a clock that never ran. */
function useOnStop(active: boolean, root: RefObject<SVGSVGElement | null>, onStopped: (el: SVGSVGElement) => void) {
  const ran = useRef(false);
  const stopped = useEffectEvent(onStopped);
  useEffect(() => {
    if (active) ran.current = true;
    else if (ran.current && root.current) {
      ran.current = false;
      stopped(root.current);
    }
  }, [active, root]);
}

/** Whether a frame at `now` is to be drawn: every one, or with `idleOnly` one every IDLE_FRAME_MS. */
function usePace(idleOnly: boolean): (now: number) => boolean {
  const drawnAt = useRef(-Infinity);
  return (now) => {
    if (idleOnly && now - drawnAt.current < IDLE_FRAME_MS) return false;
    drawnAt.current = now;
    return true;
  };
}

/**
 * Runs the plate's tracks while `moving`, the screen plays motion and the plate is on screen; puts everything still
 * when it stops. `idleOnly`: nothing moves but the idle sway, so a frame is drawn at most every IDLE_FRAME_MS.
 */
export default function usePlateClock(
  root: RefObject<SVGSVGElement | null>,
  moving: boolean,
  { onFrame, onStop }: ClockHooks = {},
  { idleOnly = false }: { idleOnly?: boolean } = {},
): void {
  const still = useHoldsStill();
  const seen = useOnScreen(root);
  const active = moving && !still && seen;
  const due = usePace(idleOnly);
  // The tracked elements, found again after every render (a condition added or gone changes them; React may have
  // written their still values back) - so each frame only samples and writes.
  const entries = useRef<Entry[]>([]);
  useLayoutEffect(() => {
    entries.current = root.current ? collectTracks(root.current) : [];
  });
  useOnStop(active, root, (el) => {
    writeTracks(entries.current);
    onStop?.(el);
  });
  useDisplayClock(active, (now: number) => {
    if (!root.current || !due(now)) return;
    onFrame?.(now / 1000, root.current);
    writeTracks(entries.current, now / 1000);
  });
}
